import { useLocalStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import type { MeasurementSystem, TimeConvention } from "@shared/types/config";
import type { CurrencyUnit } from "@shared/generated/graphql/chargetrip";
import { type VehicleConnector } from "@shared/generated/graphql/chargetrip";
import type {
  PersistCookies,
  PersistTooltips,
  CompletedOnboardingSteps,
} from "./types";
import { CONFIG } from "@shared/config";
import type { ProductMode } from "@shared/config/types";
import type { SearchResult } from "@shared/components/location-input/types";
import { v4 as uuidv4 } from "uuid";
import { computed, watch } from "vue";
import { PLAN_PATH, TRUCK_PLAN_PATH } from "@shared/utils/product-mode";
import { setContext } from "@sentry/vue";
import type { ChargeCard } from "@shared/components/charge-card-select/types";

import {
  getDefaultChargeCards,
  isSameChargeCard,
} from "@shared/store/persist/helpers";
import { addSignalAttribute } from "@dash0/sdk-web";
import type { TemperatureUnit } from "@shared/composables/conversion/use-temperature/types";
import { isAndroid } from "@chargetrip/frontend-utilities";

const {
  application: {
    initialization: {
      haptics,
      ui: { onboarding },
      tracking: { isSentryEnabled, isDash0Enabled },
      localization: {
        language,
        measurementSystem,
        temperatureConvention,
        timeConvention,
        currency,
      },
      vehicle,
      defaultProductMode,
      productModes,
    },
  },
  features: {
    station: {
      pricing: { preferredChargeCards },
    },
  },
} = CONFIG;

const MAX_SAVED_VEHICLES = 3;

const { VITE_APP_NAME } = import.meta.env;

interface Persist {
  uuid: string;
  locale: string;
  measurementSystem: MeasurementSystem;
  temperatureUnit: TemperatureUnit;
  timeConvention: TimeConvention;
  selectedVehicleId?: string;
  customRangeOffsetInPercentages: number;
  selectedCurrency: CurrencyUnit;
  productMode: ProductMode;
  savedVehicleAdapters: Array<
    Pick<VehicleConnector, "standard" | "power" | "time">
  >;
  showIncompatibleConnectors: boolean;
  selectedChargeCards: ChargeCard[];
  selectedChargeCard: ChargeCard;
  lastSeenRelease: string;
  showChargeCardTooltip: boolean;
  savedSearchResults: Array<SearchResult>;
  savedSelectedVehicleIds: Array<string>;
  acceptedCookies?: PersistCookies;
  tooltips?: PersistTooltips;
  placesSessionToken: string;
  completedOnboardingSteps: CompletedOnboardingSteps;
  traffic: boolean;
  mapStyle: "default" | "satellite";
  haptics: boolean;
}

export const usePersistStore = defineStore("persist", () => {
  const defaultChargeCards = getDefaultChargeCards();

  const persist = useLocalStorage<Persist>(
    `${VITE_APP_NAME}-persist`,
    {
      uuid: uuidv4(),
      locale: language.defaultValue,
      measurementSystem: measurementSystem.defaultValue,
      temperatureUnit: temperatureConvention.defaultValue,
      timeConvention: timeConvention.defaultValue,
      selectedCurrency: currency.defaultValue,
      customRangeOffsetInPercentages: 100,
      selectedChargeCard: defaultChargeCards[0],
      selectedChargeCards: defaultChargeCards,
      selectedVehicleId: vehicle?.id?.defaultValue,
      productMode: defaultProductMode ?? "personal",
      savedVehicleAdapters: [],
      showIncompatibleConnectors: false,
      savedSearchResults: [],
      savedSelectedVehicleIds: [],
      lastSeenRelease: "",
      showChargeCardTooltip: true,
      placesSessionToken: uuidv4(),
      completedOnboardingSteps: {
        slider: onboarding.slider.isHidden,
        vehicles: Boolean(
          onboarding.vehicles.isHidden || vehicle?.id?.defaultValue,
        ),
        cookies: onboarding.cookies.isHidden,
        welcome: onboarding.welcome.isHidden,
      },
      acceptedCookies: {
        advertising: false,
        marketing: false,
        performance: false,
        analytics: false,
      },
      traffic: false,
      mapStyle: "default",
      haptics: isAndroid() ? haptics : false,
    },
    { mergeDefaults: true },
  );

  const storedProductMode = persist.value.productMode.toLowerCase();
  if (storedProductMode === "commercial") {
    persist.value.productMode = "commercial";
  } else {
    persist.value.productMode = "personal";
  }

  watch(
    persist,
    (value) => {
      if (isSentryEnabled) {
        setContext("persist-store", { value: JSON.stringify(value, null, 4) });
      }
      if (isDash0Enabled) {
        addSignalAttribute("persist", JSON.stringify(value, null, 4));
      }
    },
    { deep: true, immediate: true },
  );

  const isSameCards =
    JSON.stringify(defaultChargeCards) ===
    JSON.stringify(persist.value.selectedChargeCards);

  // If the app doesn't have ability to add new chargecards, and the storage doesn't match the config cards, we want to reset it.
  if (preferredChargeCards.isHidden && !isSameCards) {
    persist.value.selectedChargeCards = defaultChargeCards;
    persist.value.selectedChargeCard = defaultChargeCards[0];
  }

  if (persist.value.savedSearchResults.some((result) => !result.label)) {
    persist.value.savedSearchResults = [];
  }

  watch(
    () => persist.value.selectedChargeCards,
    (selectedChargeCards, previousSelectedChargeCards) => {
      // When a new charge card is added, select it automatically.
      if (selectedChargeCards.length > previousSelectedChargeCards.length) {
        persist.value.selectedChargeCard = selectedChargeCards[0];
        return;
      }

      // When a charge card is removed, check if the selected was removed, if so select the first one again.
      const { selectedChargeCard } = persist.value;
      const isInList = selectedChargeCards.some((card) =>
        isSameChargeCard(card, selectedChargeCard),
      );

      if (isInList) return;

      persist.value.selectedChargeCard = selectedChargeCards[0];
    },
  );

  const setMapStyle = (value: "default" | "satellite"): void => {
    persist.value.mapStyle = value;
  };

  const setTraffic = (value: boolean): void => {
    persist.value.traffic = value;
  };

  const setLocale = (value: string): void => {
    persist.value.locale = value;
  };

  const setLastSeenRelease = (value: string): void => {
    persist.value.lastSeenRelease = value;
  };

  const setMeasurementSystem = (value: MeasurementSystem): void => {
    persist.value.measurementSystem = value;
  };

  const setTemperatureUnit = (value: TemperatureUnit): void => {
    persist.value.temperatureUnit = value;
  };

  const setTimeConvention = (value: TimeConvention): void => {
    persist.value.timeConvention = value;
  };

  const setCurrency = (value: CurrencyUnit): void => {
    persist.value.selectedCurrency = value;
  };

  const setCookiesFromArray = (values: (keyof PersistCookies)[]): void => {
    const result: PersistCookies = {
      advertising: false,
      marketing: false,
      performance: false,
      analytics: false,
    };

    for (const value of values) {
      result[value] = true;
    }

    persist.value.acceptedCookies = result;
  };

  const setCookiesFromObject = (value: PersistCookies): void => {
    persist.value.acceptedCookies = value;
  };

  const setCustomRangeOffsetInPercentages = (value: number): void => {
    persist.value.customRangeOffsetInPercentages = value;
  };

  const setSelectedVehicleId = (value: string): void => {
    persist.value.selectedVehicleId = value;
  };

  const setTooltip = (tooltip: keyof PersistTooltips, value: boolean): void => {
    persist.value.tooltips = {
      ...persist.value.tooltips,
      [tooltip]: value,
    };
  };

  const setPlacesSessionToken = (): void => {
    persist.value.placesSessionToken = uuidv4();
  };

  const setProductMode = (value: ProductMode): void => {
    if (!productModes.includes(value)) return;

    persist.value.productMode = value;
  };

  const isTruckRoute = computed(
    () => persist.value.productMode === "commercial",
  );

  const planPath = computed(() =>
    isTruckRoute.value ? TRUCK_PLAN_PATH : PLAN_PATH,
  );

  const saveVehicleAdapters = (
    value: Array<Pick<VehicleConnector, "standard" | "power" | "time">>,
  ): void => {
    persist.value.savedVehicleAdapters = value;
  };

  const saveSearchResult = (value: SearchResult): void => {
    const hasSearchResult = persist.value.savedSearchResults
      // FIXME: Ensure clients with invalid persisted state (localStorage) do
      // not retrieve invalid `null` data. This does not affect clients other
      // than Go.
      .filter((value) => value !== null)
      .find(({ id }) => id === value.id);

    if (hasSearchResult) {
      return;
    }

    persist.value.savedSearchResults = persist.value.savedSearchResults
      .slice(-4)
      .concat(value);
  };

  const removeSavedSearchResult = (id: SearchResult["id"]): void => {
    persist.value.savedSearchResults = persist.value.savedSearchResults.filter(
      ({ id: searchResultId }) => searchResultId !== id,
    );
  };

  const deleteSavedVehicleId = (id: string): void => {
    persist.value.savedSelectedVehicleIds =
      persist.value.savedSelectedVehicleIds.filter(
        (vehicleId) => vehicleId !== id,
      );
  };

  const saveSelectedVehicleId = (id: string): void => {
    const hasSelectedVehicleId =
      persist.value.savedSelectedVehicleIds.includes(id);

    if (hasSelectedVehicleId) {
      return;
    }

    persist.value.savedSelectedVehicleIds =
      persist.value.savedSelectedVehicleIds
        .slice(-(Math.max(1, MAX_SAVED_VEHICLES) - 1))
        .concat(id);
  };

  return {
    persist,
    isTruckRoute,
    planPath,
    setLastSeenRelease,
    removeSavedSearchResult,
    saveSearchResult,
    setLocale,
    saveVehicleAdapters,
    deleteSavedVehicleId,
    saveSelectedVehicleId,
    setMeasurementSystem,
    setTemperatureUnit,
    setTimeConvention,
    setCurrency,
    setCookiesFromArray,
    setCookiesFromObject,
    setSelectedVehicleId,
    setCustomRangeOffsetInPercentages,
    setTooltip,
    setPlacesSessionToken,
    setProductMode,
    setTraffic,
    setMapStyle,
  };
});
